import {
  getImage,
  getNestedFloatingElementsByType,
  getNestedSubelementsByType,
  getPersonName,
  hasName,
} from '@fs/zion-sls'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const useFamilyReviewRelationshipOptions = ({
  images,
  mainPartitionKey,
  splitScreenPartitionKey,
  primaryPersonId,
  currentRecordId,
  includeRecordId = false,
}) => {
  const [t] = useTranslation()

  // Populates the FormAutosuggest with (respectively) Names in current person's record, CurrentImage, and SplitScreenImage
  // Intentionally leaves in duplicates (found in this record, but also on the current/split screen page)
  const relationshipOptions = useMemo(() => {
    if (!images || !mainPartitionKey) return []

    const currentImage = getImage(images, mainPartitionKey)
    const splitScreenImage = getImage(images, splitScreenPartitionKey)

    const getGroupInformation = (persons) => {
      return (
        persons
          .sort((a, b) => (a?.primary > b?.primary ? -1 : 1))
          // Returns the suggestion object used in FormAutoSugggest
          .map((person) => {
            let groupName = ''
            let sortKey = null
            if (person.recordId === currentRecordId) {
              groupName = t('edit-relationships.autosuggest.record-group', 'IN THIS RECORD')
              sortKey = 0
            } else if (person.partitionKey === splitScreenPartitionKey) {
              groupName = t('edit-relationships.autosuggest.image-group.split-screen.simple', 'NAMES ON SPLIT SCREEN')
              sortKey = 2
            } else {
              groupName = t('edit-relationships.autosuggest.image-group.this', 'IN THIS IMAGE')
              sortKey = 1
            }
            const obj = {
              key: person.id,
              primaryText: getPersonName({ person, justOneName: true }),
              group: groupName,
              sortKey,
            }

            if (includeRecordId) obj.recordId = person.recordId

            // If the person is part of the record, AND is on the split screen, add a secondary text to indicate that they are on the split screen
            if (person.recordId === currentRecordId && person.partitionKey === splitScreenPartitionKey)
              obj.secondaryText = t('family-review.edit-relationships.autosuggest.split-screen', '(Split Screen)')
            // Add (primary) indicator as secondary text to head of household
            else if (person.primary) obj.secondaryText = t('edit-relationships.autosuggest.primary', '(Principal)')
            return obj
          })
          .filter((opt) => hasName(opt.primaryText.trim()))
      )
    }

    let relationshipOpts = [
      ...(currentImage?.subElements
        ? currentImage.subElements.flatMap((record) =>
            getGroupInformation(getNestedSubelementsByType(record, 'PERSON').filter(({ id }) => id !== primaryPersonId))
          )
        : []),
      ...(currentImage?.floatingElements
        ? getGroupInformation(
            getNestedFloatingElementsByType(currentImage, 'PERSON').filter(({ id }) => id !== primaryPersonId)
          )
        : []),
    ]

    if (!splitScreenImage) return relationshipOpts
    relationshipOpts = relationshipOpts
      .concat(
        splitScreenImage?.subElements
          ? splitScreenImage.subElements?.flatMap((record) =>
              getGroupInformation(getNestedSubelementsByType(record, 'PERSON'))
            )
          : []
      )
      .concat(
        splitScreenImage?.floatingElements
          ? getGroupInformation(getNestedFloatingElementsByType(splitScreenImage, 'PERSON'))
          : []
      )

    return relationshipOpts.sort((a, b) => a.sortKey - b.sortKey)
  }, [images, mainPartitionKey, splitScreenPartitionKey, primaryPersonId, t, currentRecordId, includeRecordId])

  return relationshipOptions
}

export default useFamilyReviewRelationshipOptions
